import React, { memo, useEffect, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { esDuenoAgente, tieneAlgunRol, rutaDeInicioDuenoAgente } from '@/shared/utils/oficio';
import { useDispatch, useSelector } from 'react-redux';
import {
  X,
  Home,
  Building2,
  Users,
  BarChart3,
  Settings,
  CreditCard,
  Globe,
  Megaphone,
  Package,
  DollarSign,
  Calendar,
  FileText,
  ClipboardList,
  Shield,
  Wrench,
  Sparkles,
  LogIn,
  AlertTriangle,
  Building,
  Heart,
  Search,
  UserCog,
  Receipt,
  Repeat,
  ScrollText,
  Activity,
  TrendingUp,
  MessageSquare,
  Key,
  LayoutTemplate,
  Palette,
  Mail,
  ChevronDown,
  GitBranch,
  MessageCircle,
} from 'lucide-react';
import { User, Tenant } from '@/shared/types';
import { APP_CONFIG } from '@/shared/config/config';
import { cn } from '@/shared/utils/cn';
import { fetchTrialDaysRequest } from '@/features/billing/redux/trial-slice';
import { selectTrialInfo } from '@/features/billing/redux/trial-selectors';
import { AppDispatch } from '@/shared/store/store';
import { WorkspaceSelectorCard } from '@/features/impersonation/components/WorkspaceSelectorCard';
import { openSelector } from '@/features/impersonation/redux/impersonation-slice';
import {
  SECCIONES_AGENCIA_GHL,
  CONFIGURACION_GHL,
  ROLES_MENU_GHL,
  TODOS_LOS_ITEMS_GHL,
  itemActivoDe,
  NavItemGhl,
} from '../config/agency-ghl-nav';

export interface SidebarProps {
  user: User | null;
  tenant: Tenant | null;
  onClose: () => void;
  isMobile?: boolean;
  collapsed?: boolean;
  className?: string;
}

interface NavigationItem {
  name: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  roles: string[];
  badge?: string;
}

/**
 * Agrupación del nav por módulo (UX 2026-07-11). El sidebar tenía ~29 items
 * planos para AGENCY_ADMIN → largo y difícil de escanear. En vez de taguear los
 * ~60 items uno por uno, se deriva el grupo por keyword del href (mantenible +
 * diff chico). El orden del array es el orden de las secciones y de evaluación
 * (primer match gana). 'principal' se muestra sin header (siempre visible).
 */
interface NavGroup {
  id: string;
  label: string;
  match: (href: string) => boolean;
}

const NAV_GROUPS: NavGroup[] = [
  {
    id: 'principal',
    label: '',
    // OJO: excluir /erp/* — '/erp/dashboard' matcheaba '/dashboard' y el ERP
    // caía en Principal en vez de Contabilidad & DGII (bug cazado en E2E).
    match: (h) => !h.startsWith('/erp') && /\/(dashboard|inbox)(\/|$)/.test(h),
  },
  { id: 'sitio', label: 'Sitio Web', match: (h) => /\/(builder|templates|apariencia|webpage)(\/|$)/.test(h) },
  {
    id: 'marketing',
    label: 'Marketing',
    // Mantener esta lista al agregar rutas de marketing; si no, el item cae en el
    // grupo fallback 'Gestion' (paso con /agencia/whatsapp-plantillas).
    match: (h) =>
      /\/(redes|email-campaigns|secuencias|enlaces-pago|asistente-ia|whatsapp-plantillas|publicidad)(\/|$)/.test(h),
  },
  { id: 'reportes', label: 'Reportes', match: (h) => /\/reportes(\/|$)/.test(h) },
  { id: 'finanzas', label: 'Contabilidad & DGII', match: (h) => /\/(erp|accounting|facturacion|facturas|pagos|comisiones|cobros)/.test(h) },
  { id: 'ajustes', label: 'Administración', match: (h) => /\/(configuracion|api-keys|admin\/system|integrations|modules|subscription|billing|tenants|admin\/webpage)/.test(h) },
  { id: 'gestion', label: 'Gestión', match: () => true }, // fallback: inventario/agentes/clientes/crm/agenda/etc.
];

const groupOf = (href: string): string =>
  (NAV_GROUPS.find((g) => g.match(href)) || NAV_GROUPS[NAV_GROUPS.length - 1]).id;

// Orden de DISPLAY de las secciones (distinto del de evaluación: 'gestion' es
// fallback y va último al evaluar, pero se muestra arriba tras 'principal').
const GROUP_DISPLAY_ORDER = [
  'principal',
  'gestion',
  'marketing',
  'reportes',
  'sitio',
  'finanzas',
  'ajustes',
];
const labelOf = (id: string): string => NAV_GROUPS.find((g) => g.id === id)?.label ?? '';

const Sidebar: React.FC<SidebarProps> = ({
  user,
  tenant,
  onClose,
  isMobile,
  collapsed = false,
  className,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const trialInfo = useSelector(selectTrialInfo);

  // Cargar y actualizar días de prueba periódicamente
  useEffect(() => {
    if (user && user.role !== 'SUPER_ADMIN') {
      // Actualizar inmediatamente al montar
      dispatch(fetchTrialDaysRequest());

      // Calcular tiempo hasta la próxima medianoche para actualizar cuando cambie el día
      const now = new Date();
      const tomorrow = new Date(now);
      tomorrow.setDate(tomorrow.getDate() + 1);
      tomorrow.setHours(0, 0, 0, 0);
      const msUntilMidnight = tomorrow.getTime() - now.getTime();

      // Actualizar a medianoche (cuando cambia el día)
      const midnightTimeout = setTimeout(() => {
        dispatch(fetchTrialDaysRequest());
      }, msUntilMidnight);

      // También actualizar cada hora como respaldo para mantener los datos frescos
      const hourlyInterval = setInterval(() => {
        dispatch(fetchTrialDaysRequest());
      }, 60 * 60 * 1000); // Cada hora

      return () => {
        clearTimeout(midnightTimeout);
        clearInterval(hourlyInterval);
      };
    }
  }, [dispatch, user]);

  // Obtener días de prueba restantes desde Redux
  const trialDaysRemaining = trialInfo.isInTrial ? trialInfo.daysRemaining : null;

  // Grupos colapsados del sidebar (persistido).
  // Default: COLAPSADOS (pedido de Randhy 2026-07-11: "que parezcan así
  // colapsados, que es más ordenado"). El grupo de la ruta activa se auto-expande,
  // así que nunca escondemos dónde estás parado.
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(() => {
    const allCollapsed = () =>
      new Set(NAV_GROUPS.filter((g) => g.label).map((g) => g.id));
    try {
      const raw = localStorage.getItem('inbo_sidebar_collapsed_groups');
      return raw ? new Set(JSON.parse(raw) as string[]) : allCollapsed();
    } catch {
      return allCollapsed();
    }
  });

  const toggleGroup = (groupId: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) next.delete(groupId);
      else next.add(groupId);
      try {
        localStorage.setItem(
          'inbo_sidebar_collapsed_groups',
          JSON.stringify([...next])
        );
      } catch {
        /* noop */
      }
      return next;
    });
  };

  // Definir navegación por rol
  const navigationItems: NavigationItem[] = [
    {
      name: 'Dashboard',
      href:
        rutaDeInicioDuenoAgente(user) ??
        (user?.role === 'SUPER_ADMIN'
          ? '/admin/dashboard'
          : user?.role === 'PROJECT_DEVELOPER_ADMIN'
            ? '/promotora/dashboard'
            : user?.role === 'AGENCY_ADMIN'
              ? '/agencia/dashboard'
              : user?.role === 'BROKER_MANAGER_ADMIN'
                ? '/broker/dashboard'
                : user?.role === 'AGENCY_AGENT_USER'
                  ? '/agente/dashboard'
                  : user?.role === 'LEGAL_MODULE'
                    ? '/legal/dashboard'
                    : user?.role === 'ACCOUNTING_TEAM'
                      ? '/accounting/dashboard'
                      : user?.role === 'OPERATIONAL_TEAM'
                        ? '/operational/dashboard'
                        : user?.role === 'BUYER_CLIENT'
                          ? '/client/dashboard'
                          : // user?.role === 'SELLER_CLIENT' ? '/seller/dashboard' : // SELLER_CLIENT role removed
                          user?.role === 'CONDOMINIUM_ADMIN'
                            ? '/condominium/dashboard'
                            : '/agencia/dashboard'),
      icon: Home,
      roles: [
        'SUPER_ADMIN',
        'AGENCY_ADMIN',
        'BROKER_MANAGER_ADMIN',
        'PROJECT_DEVELOPER_ADMIN',
        'AGENCY_AGENT_USER',
        'LEGAL_MODULE',
        'ACCOUNTING_TEAM',
        'OPERATIONAL_TEAM',
        'BUYER_CLIENT',
        'CONDOMINIUM_ADMIN',
      ],
    },
    {
      name: 'Inbox',
      href: '/agencia/inbox',
      icon: MessageSquare,
      roles: ['AGENCY_ADMIN'],
    },
    {
      name: 'Inventario',
      href: '/agencia/inventario',
      icon: Building2,
      roles: ['AGENCY_ADMIN'],
    },
    {
      name: 'Agentes',
      href: '/agencia/agentes',
      icon: Users,
      roles: ['AGENCY_ADMIN'],
    },
    {
      name: 'Clientes',
      href: '/agencia/clientes',
      icon: Users,
      roles: ['AGENCY_ADMIN'],
    },
    {
      name: 'CRM Pipeline',
      href: '/agencia/crm',
      icon: TrendingUp,
      roles: ['AGENCY_ADMIN', 'BROKER_MANAGER_ADMIN'],
    },
    {
      // #870 — embudos configurables por campana/proyecto (pedido de Gabriel)
      name: 'Embudos',
      href: '/agencia/crm/embudos',
      icon: GitBranch,
      roles: ['AGENCY_ADMIN', 'BROKER_MANAGER_ADMIN'],
    },
    {
      // Ola 2 GHL #5 — Mi Agenda (vista lista de citas).
      name: 'Agenda',
      href: '/agencia/agenda',
      icon: Calendar,
      roles: ['AGENCY_ADMIN', 'BROKER_MANAGER_ADMIN'],
    },
    {
      // #874 — Calendarios de reserva (links públicos tipo Calendly).
      name: 'Calendarios',
      href: '/agencia/calendarios',
      icon: Calendar,
      roles: ['AGENCY_ADMIN', 'BROKER_MANAGER_ADMIN'],
    },
    {
      // #873 — Widget de chat embebible para sitios externos.
      name: 'Widget de chat',
      href: '/agencia/widget',
      icon: MessageSquare,
      roles: ['AGENCY_ADMIN', 'BROKER_MANAGER_ADMIN'],
    },
    {
      name: 'Finanzas',
      href: '/agencia/erp',
      icon: Receipt,
      roles: ['AGENCY_ADMIN', 'ACCOUNTING_TEAM'],
    },
    {
      name: 'Nuevo Documento',
      href: '/agencia/erp/wizard',
      icon: FileText,
      roles: ['AGENCY_ADMIN', 'ACCOUNTING_TEAM', 'BROKER_MANAGER_ADMIN'],
    },
    {
      name: 'Contratos',
      href: '/agencia/documentos',
      icon: ScrollText,
      roles: ['AGENCY_ADMIN'],
    },
    {
      name: 'Marketing',
      href: '/agencia/redes',
      icon: Megaphone,
      roles: [
        'AGENCY_ADMIN',
        'BROKER_MANAGER_ADMIN',
        'AGENCY_AGENT_USER',
        'ACCOUNTING_TEAM',
      ],
    },
    {
      // Ola 2 GHL #6 — Campañas de Email (marketing a segmentos).
      name: 'Campañas Email',
      href: '/agencia/email-campaigns',
      icon: Mail,
      // = los roles de la ruta (ROLES_EMAIL_CAMPAIGNS, #1658). BROKER caía en /unauthorized.
      roles: ['AGENCY_ADMIN'],
    },
    {
      // #882 — plantillas de WhatsApp aprobadas por Meta (fuera de la ventana 24h)
      name: 'Plantillas WhatsApp',
      href: '/agencia/whatsapp-plantillas',
      icon: MessageCircle,
      roles: ['AGENCY_ADMIN', 'BROKER_MANAGER_ADMIN'],
    },
    {
      // #876 — qué anuncio trae compradores (costo por lead), no cuál tiene clics.
      name: 'Publicidad',
      href: '/agencia/publicidad',
      icon: Megaphone,
      roles: ['AGENCY_ADMIN', 'BROKER_MANAGER_ADMIN'],
    },
    {
      // Ola 2 GHL #8 — Secuencias de pipeline (drip automation).
      name: 'Secuencias',
      href: '/agencia/secuencias',
      icon: Repeat,
      roles: ['AGENCY_ADMIN', 'BROKER_MANAGER_ADMIN'],
    },
    {
      // Ola 2 GHL #9 — Enlaces de pago (PayPal.me).
      name: 'Enlaces de pago',
      href: '/agencia/enlaces-pago',
      icon: CreditCard,
      roles: ['AGENCY_ADMIN', 'BROKER_MANAGER_ADMIN'],
    },
    {
      // Ola 2 GHL #11 — Config del Asistente IA (self-service).
      name: 'Asistente IA',
      href: '/agencia/asistente-ia',
      icon: Sparkles,
      roles: ['AGENCY_ADMIN', 'BROKER_MANAGER_ADMIN'],
    },
    {
      name: 'Reportes',
      href: '/agencia/reportes',
      icon: BarChart3,
      roles: ['AGENCY_ADMIN'],
    },
    {
      name: 'Builder',
      href: '/agencia/builder',
      icon: Globe,
      // El builder es tenant-scoped (API sólo JwtAuthGuard + req.tenantId, sin
      // role-gate). Las promotoras (PROJECT_DEVELOPER_ADMIN) también editan su
      // propio sitio → capacidad permanente, no sólo agencias. (2026-07-07)
      roles: ['AGENCY_ADMIN', 'PROJECT_DEVELOPER_ADMIN'],
    },
    {
      name: 'Templates',
      href: '/agencia/templates',
      icon: LayoutTemplate,
      roles: ['AGENCY_ADMIN'],
    },
    {
      // #792 Theme Options — la página /agencia/apariencia existía pero no era
      // alcanzable desde el nav. Junto a Builder/Templates (customización del sitio).
      name: 'Apariencia',
      href: '/agencia/apariencia',
      icon: Palette,
      roles: ['AGENCY_ADMIN'],
    },
    {
      name: 'API Keys',
      href: '/agencia/api-keys',
      icon: Key,
      roles: ['AGENCY_ADMIN'],
    },
    {
      name: 'Configuración',
      href: '/agencia/configuracion',
      icon: Settings,
      roles: ['AGENCY_ADMIN'],
    },
    // Promotora de Proyectos específico
    {
      name: 'Proyectos',
      href: '/promotora/proyectos',
      icon: Building,
      roles: ['PROJECT_DEVELOPER_ADMIN'],
    },
    {
      name: 'Unidades',
      href: '/promotora/unidades',
      icon: Package,
      roles: ['PROJECT_DEVELOPER_ADMIN'],
    },
    {
      name: 'Leads & CRM',
      href: '/promotora/leads',
      icon: Users,
      roles: ['PROJECT_DEVELOPER_ADMIN'],
    },
    {
      name: 'Agencias Asociadas',
      href: '/promotora/agencias',
      icon: Building2,
      roles: ['PROJECT_DEVELOPER_ADMIN'],
    },
    {
      name: 'Facturación',
      href: '/promotora/facturacion',
      icon: CreditCard,
      roles: ['PROJECT_DEVELOPER_ADMIN'],
    },
    {
      name: 'Reportes',
      href: '/promotora/reportes',
      icon: BarChart3,
      roles: ['PROJECT_DEVELOPER_ADMIN'],
    },
    {
      name: 'Configuración',
      href: '/promotora/configuracion',
      icon: Settings,
      roles: ['PROJECT_DEVELOPER_ADMIN'],
    },
    // Broker Manager específico
    {
      name: 'Propiedades',
      href: '/broker/propiedades',
      icon: Building2,
      roles: ['BROKER_MANAGER_ADMIN'],
    },
    {
      name: 'Agentes',
      href: '/broker/agentes',
      icon: Users,
      roles: ['BROKER_MANAGER_ADMIN'],
    },
    {
      name: 'Leads del equipo',
      href: '/broker/leads',
      icon: Users,
      roles: ['BROKER_MANAGER_ADMIN'],
    },
    {
      name: 'Comisiones',
      href: '/broker/comisiones',
      icon: DollarSign,
      roles: ['BROKER_MANAGER_ADMIN'],
    },
    {
      name: 'Reportes',
      href: '/broker/reportes',
      icon: BarChart3,
      roles: ['BROKER_MANAGER_ADMIN'],
    },
    {
      name: 'Configuración',
      href: '/broker/configuracion',
      icon: Settings,
      roles: ['BROKER_MANAGER_ADMIN'],
    },
    // Agente Inmobiliario específico
    {
      name: 'Mis Propiedades',
      href: '/agente/propiedades',
      icon: Building2,
      roles: ['AGENCY_AGENT_USER'],
    },
    {
      name: 'Mis Clientes',
      href: '/agente/clientes',
      icon: Users,
      roles: ['AGENCY_AGENT_USER'],
    },
    {
      name: 'Cotizaciones',
      href: '/agente/cotizaciones',
      icon: FileText,
      roles: ['AGENCY_AGENT_USER'],
    },
    {
      name: 'Citas & Visitas',
      href: '/agente/citas',
      icon: Calendar,
      roles: ['AGENCY_AGENT_USER'],
    },
    {
      name: 'Mis Ventas',
      href: '/agente/ventas',
      icon: DollarSign,
      roles: ['AGENCY_AGENT_USER'],
    },
    {
      name: 'Reportes',
      href: '/agente/reportes',
      icon: BarChart3,
      roles: ['AGENCY_AGENT_USER'],
      // GH#2443: la pantalla dice «Próximamente» (GH#2307); el menú lo dice antes de entrar. Se conserva el enlace.
      badge: 'Próximamente',
    },
    {
      name: 'Configuración',
      href: '/agente/configuracion',
      icon: Settings,
      roles: ['AGENCY_AGENT_USER'],
    },
    // Módulo Legal específico
    {
      name: 'Contratos',
      href: '/legal/contratos',
      icon: FileText,
      roles: ['LEGAL_MODULE'],
    },
    {
      name: 'Plantillas',
      href: '/legal/plantillas',
      icon: ClipboardList,
      roles: ['LEGAL_MODULE'],
    },
    {
      name: 'Auditoría Legal',
      href: '/legal/auditoria',
      icon: Shield,
      roles: ['LEGAL_MODULE'],
    },
    {
      name: 'Configuración',
      href: '/legal/configuracion',
      icon: Settings,
      roles: ['LEGAL_MODULE'],
    },
    // Equipo Contable específico
    {
      name: 'Facturas',
      href: '/accounting/facturas',
      icon: FileText,
      roles: ['ACCOUNTING_TEAM'],
    },
    {
      name: 'Pagos',
      href: '/accounting/pagos',
      icon: CreditCard,
      roles: ['ACCOUNTING_TEAM'],
    },
    {
      name: 'Reportes',
      href: '/accounting/reportes',
      icon: BarChart3,
      roles: ['ACCOUNTING_TEAM'],
    },
    {
      name: 'Configuración',
      href: '/accounting/configuracion',
      icon: Settings,
      roles: ['ACCOUNTING_TEAM'],
    },
    // Equipo Operativo específico
    {
      name: 'Mantenimiento',
      href: '/operational/mantenimiento',
      icon: Wrench,
      roles: ['OPERATIONAL_TEAM'],
    },
    {
      name: 'Limpieza',
      href: '/operational/limpieza',
      icon: Sparkles,
      roles: ['OPERATIONAL_TEAM'],
    },
    {
      name: 'Check-in/Check-out',
      href: '/operational/checkin',
      icon: LogIn,
      roles: ['OPERATIONAL_TEAM'],
    },
    {
      name: 'Incidencias',
      href: '/operational/incidencias',
      icon: AlertTriangle,
      roles: ['OPERATIONAL_TEAM'],
    },
    {
      name: 'Configuración',
      href: '/operational/configuracion',
      icon: Settings,
      roles: ['OPERATIONAL_TEAM'],
    },
    // Cliente Vendedor específico - COMENTADO: SELLER_CLIENT role eliminado
    /*
    {
      name: 'Mis Propiedades',
      href: '/seller/propiedades',
      icon: Home,
      roles: ['SELLER_CLIENT'],
    },
    {
      name: 'Mis Ofertas',
      href: '/seller/ofertas',
      icon: Hand,
      roles: ['SELLER_CLIENT'],
    },
    {
      name: 'Negociaciones',
      href: '/seller/negociaciones',
      icon: MessageSquare,
      roles: ['SELLER_CLIENT'],
    },
    {
      name: 'Documentos',
      href: '/seller/documentos',
      icon: FileText,
      roles: ['SELLER_CLIENT'],
    },
    {
      name: 'Configuración',
      href: '/seller/configuracion',
      icon: Settings,
      roles: ['SELLER_CLIENT'],
    },
    */
    // Administrador de Condominio específico
    {
      name: 'Residentes',
      href: '/condominium/residentes',
      icon: Users,
      roles: ['CONDOMINIUM_ADMIN'],
    },
    {
      name: 'Unidades',
      href: '/condominium/unidades',
      icon: Building,
      roles: ['CONDOMINIUM_ADMIN'],
    },
    {
      name: 'Pagos',
      href: '/condominium/pagos',
      icon: DollarSign,
      roles: ['CONDOMINIUM_ADMIN'],
    },
    {
      name: 'Facturación',
      href: '/condominium/facturacion',
      icon: Receipt,
      roles: ['CONDOMINIUM_ADMIN'],
    },
    {
      name: 'Facturas Recurrentes',
      href: '/condominium/cobros-recurrentes',
      icon: Repeat,
      roles: ['CONDOMINIUM_ADMIN'],
    },
    {
      name: 'Mantenimiento',
      href: '/condominium/mantenimiento',
      icon: Wrench,
      roles: ['CONDOMINIUM_ADMIN'],
    },
    {
      name: 'Administración',
      href: '/condominium/administracion',
      icon: FileText,
      roles: ['CONDOMINIUM_ADMIN'],
    },
    {
      name: 'Colaboradores',
      href: '/condominium/colaboradores',
      icon: UserCog,
      roles: ['CONDOMINIUM_ADMIN'],
    },
    {
      name: 'Residenciales',
      href: '/condominium/residenciales',
      icon: Building,
      roles: ['CONDOMINIUM_ADMIN'],
    },
    {
      name: 'Proveedores',
      href: '/condominium/proveedores',
      icon: UserCog,
      roles: ['CONDOMINIUM_ADMIN'],
    },
    {
      name: 'Configuración',
      href: '/condominium/configuracion',
      icon: Settings,
      roles: ['CONDOMINIUM_ADMIN'],
    },
    // Super Admin específico
    {
      name: 'Clientes',
      href: '/admin/tenants',
      icon: Building2,
      roles: ['SUPER_ADMIN'],
    },
    {
      name: 'Suscripciones',
      href: '/admin/subscription-policies',
      icon: ScrollText,
      roles: ['SUPER_ADMIN'],
    },
    {
      name: 'Facturación',
      href: '/admin/billing',
      icon: Receipt,
      roles: ['SUPER_ADMIN'],
    },
    {
      name: 'Módulos',
      href: '/admin/modules',
      icon: Package,
      roles: ['SUPER_ADMIN'],
    },
    {
      name: 'Integraciones',
      href: '/admin/integrations',
      icon: Globe,
      roles: ['SUPER_ADMIN'],
    },
    {
      name: 'Constructor Web',
      href: '/admin/webpage',
      icon: FileText,
      roles: ['SUPER_ADMIN'],
    },
    {
      name: 'API Keys',
      href: '/admin/api-keys',
      icon: Key,
      roles: ['SUPER_ADMIN'],
    },
    // ERP (Sprint 8, IRIS lane) — abierto a roles de gestion fiscal.
    // Path corregido 2026-05-25: /erp/dashboard (no /admin/erp ni /agencia/erp).
    // BLOCKER 1 fix: AGENCY_ACCOUNTING no existe en enum UserRole; el rol
    // correcto para "contadora interna" es ACCOUNTING_TEAM (AG10-09 Gabriel).
    {
      name: 'ERP',
      href: '/erp/dashboard',
      icon: Receipt,
      roles: ['AGENCY_ADMIN', 'ACCOUNTING_TEAM', 'SUPER_ADMIN'],
    },
    {
      name: 'Asientos',
      href: '/erp/journal-entries',
      icon: ClipboardList,
      roles: ['AGENCY_ADMIN', 'ACCOUNTING_TEAM', 'SUPER_ADMIN'],
    },
    {
      name: 'Reportes DGII',
      href: '/erp/reports/607',
      icon: ScrollText,
      roles: ['AGENCY_ADMIN', 'ACCOUNTING_TEAM', 'SUPER_ADMIN'],
    },
    {
      name: 'Cola e-CF',
      href: '/erp/dgii/ecf',
      icon: FileText,
      roles: ['AGENCY_ADMIN', 'ACCOUNTING_TEAM', 'SUPER_ADMIN'],
    },
    {
      name: 'Certificados P12',
      href: '/erp/dgii/p12',
      icon: Shield,
      roles: ['AGENCY_ADMIN', 'ACCOUNTING_TEAM', 'SUPER_ADMIN'],
    },
    {
      name: 'Rangos NCF',
      href: '/erp/dgii/ncf',
      icon: AlertTriangle,
      roles: ['AGENCY_ADMIN', 'ACCOUNTING_TEAM', 'SUPER_ADMIN'],
    },
    {
      name: 'Plan contable',
      href: '/erp/cuentas',
      icon: LayoutTemplate,
      roles: ['AGENCY_ADMIN', 'ACCOUNTING_TEAM', 'SUPER_ADMIN'],
    },
    // ERP-8.6 — Journey Agente: tab "Mis comisiones" (unico entry ERP
    // visible para AGENCY_AGENT_USER).
    {
      name: 'Mis comisiones',
      href: '/erp/comisiones',
      icon: DollarSign,
      roles: ['AGENCY_AGENT_USER', 'AGENCY_ADMIN', 'ACCOUNTING_TEAM', 'SUPER_ADMIN'],
    },
    {
      name: 'Sistema',
      href: '/admin/system',
      icon: Activity,
      roles: ['SUPER_ADMIN'],
    },
    {
      name: 'Configuración',
      href: '/admin/configuracion',
      icon: Settings,
      roles: ['SUPER_ADMIN'],
    },
    // Cliente Final (BUYER_CLIENT) específico
    {
      name: 'Propiedades',
      href: '/client/properties',
      icon: Search,
      roles: ['BUYER_CLIENT'],
    },
    {
      name: 'Favoritos',
      href: '/client/favorites',
      icon: Heart,
      roles: ['BUYER_CLIENT'],
    },
    {
      name: 'Mis Citas',
      href: '/client/appointments',
      icon: Calendar,
      roles: ['BUYER_CLIENT'],
    },
    {
      // Sprint 10 — Modelo C Híbrido: control de vínculos con agencias de la red.
      name: 'Mis agencias',
      href: '/client/mis-agencias',
      icon: Building2,
      roles: ['BUYER_CLIENT'],
    },
    {
      // #790 #10 — búsquedas guardadas (alertas de propiedad).
      name: 'Mis búsquedas',
      href: '/client/busquedas-guardadas',
      icon: Search,
      roles: ['BUYER_CLIENT'],
    },
  ];

  // Filtrar navegación por rol del usuario
  const filteredNavigation = navigationItems.filter(item => {
    const matches = user && tieneAlgunRol(user, item.roles);
    return matches;
  });

  const isActiveRoute = (href: string) => {
    try {
      // Para rutas exactas como /admin, /dashboard, etc.
      if (href === '/admin' || href === '/dashboard') {
        return location.pathname === href;
      }
      // Para rutas con subrutas como /admin/tenants, /admin/plans, etc.
      return location.pathname.startsWith(href);
    } catch (error) {
      return false;
    }
  };

  // Agrupar el nav filtrado por módulo (orden de display), grupos vacíos fuera.
  const groupedNav = GROUP_DISPLAY_ORDER.map((id) => ({
    id,
    label: labelOf(id),
    items: filteredNavigation.filter((it) => groupOf(it.href) === id),
  })).filter((g) => g.items.length > 0);

  // El grupo de la ruta activa nunca se oculta (siempre expandido).
  const activeItem = filteredNavigation.find((it) => isActiveRoute(it.href));
  const activeGroup = activeItem ? groupOf(activeItem.href) : null;

  // GH#1643 — AGENCY_ADMIN ve el menú en el orden de GHL (config aparte, testeada).
  const duenoAgente = esDuenoAgente(user);
  const usaMenuGhl = !!user && ROLES_MENU_GHL.includes(user.role);
  // GH#1082 — el dueño-agente conserva el menú de la agencia y suma el bloque del agente (mismos ítems del menú clásico).
  const itemsAgenteGhl: NavItemGhl[] = duenoAgente
    ? navigationItems
        .filter((it) => it.href.startsWith('/agente/') && it.roles.includes('AGENCY_AGENT_USER'))
        .map((it) => ({ name: it.name, href: it.href, icon: it.icon, etiqueta: it.badge }))
    : [];
  const itemGhlActivo = usaMenuGhl ? itemActivoDe([...TODOS_LOS_ITEMS_GHL, ...itemsAgenteGhl], location.pathname) : undefined;

  const renderItemGhl = (item: NavItemGhl) => {
    const Icon = item.icon;
    if (item.proximamente || !item.href) {
      // Sin enlace a propósito: una pantalla que no existe no puede terminar en 404.
      return (
        <div
          key={item.name}
          aria-disabled="true"
          title={collapsed ? `${item.name} · Próximamente` : undefined}
          className={cn(
            'flex items-center px-3 py-2 text-sm font-medium rounded-lg text-gray-400 cursor-default',
            collapsed ? 'justify-center' : ''
          )}
        >
          <Icon className={cn('h-5 w-5 flex-shrink-0 text-gray-300', collapsed ? '' : 'mr-3')} />
          {!collapsed && (
            <>
              <span className="flex-1">{item.name}</span>
              <span className="ml-auto rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-500">
                Próximamente
              </span>
            </>
          )}
        </div>
      );
    }
    const activo = itemGhlActivo === item;
    // Link y no NavLink: NavLink marca aria-current por prefijo y pisaría el «un solo activo».
    return (
      <Link
        key={item.name + item.href}
        to={item.href}
        aria-current={activo ? 'page' : undefined}
        className={cn(
          'group flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors',
          collapsed ? 'justify-center' : '',
          activo
            ? 'bg-primary-100 text-primary-700 border-r-2 border-primary-600'
            : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
        )}
        onClick={isMobile ? onClose : undefined}
        title={collapsed ? (item.etiqueta ? `${item.name} · ${item.etiqueta}` : item.name) : undefined}
      >
        <Icon
          className={cn(
            'h-5 w-5 flex-shrink-0',
            collapsed ? '' : 'mr-3',
            activo ? 'text-primary-600' : 'text-gray-400 group-hover:text-gray-500'
          )}
        />
        {!collapsed && <span className="flex-1">{item.name}</span>}
        {!collapsed && item.etiqueta && (
          <span className="ml-auto rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-600">
            {item.etiqueta}
          </span>
        )}
      </Link>
    );
  };

  // Render de un item del nav (reutilizado en modo agrupado y colapsado).
  const renderNavItem = (item: NavigationItem) => {
    const Icon = item.icon;
    return (
      <NavLink
        key={item.name + item.href}
        to={item.href}
        className={cn(
          'group flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors',
          collapsed ? 'justify-center' : '',
          isActiveRoute(item.href)
            ? 'bg-primary-100 text-primary-700 border-r-2 border-primary-600'
            : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
        )}
        onClick={isMobile ? onClose : undefined}
        title={collapsed ? item.name : undefined}
      >
        <Icon
          className={cn(
            'h-5 w-5 flex-shrink-0',
            collapsed ? '' : 'mr-3',
            isActiveRoute(item.href)
              ? 'text-primary-600'
              : 'text-gray-400 group-hover:text-gray-500'
          )}
        />
        {!collapsed && (
          <>
            <span className="flex-1">{item.name}</span>
            {item.badge && (
              <span className="ml-auto inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
                {item.badge}
              </span>
            )}
          </>
        )}
      </NavLink>
    );
  };

  return (
    <div
      className={cn(
        'flex flex-col h-full bg-white border-r border-gray-200',
        className
      )}
    >
      {/* Header del sidebar */}
      <div className="flex items-center justify-between px-4 h-16 border-b border-gray-200">
        <div className="flex items-center justify-center w-full h-8">
          {collapsed ? (
            // Isotype cuando está colapsado
            <img src="/isotype.svg" alt="InboMarket" className="h-8 w-8" />
          ) : (
            // Logotype cuando está expandido
            <img src="/logotype.svg" alt="InboMarket" className="h-8 w-auto" />
          )}
        </div>


        {isMobile && (
          <button
            onClick={onClose}
            className="p-1 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100"
          >
            <X className="h-5 w-5" />
          </button>
        )}
      </div>


      <WorkspaceSelectorCard
        user={user}
        collapsed={collapsed}
        onOpenSelector={() => dispatch(openSelector())}
      />

      {/* Navegación — agrupada por módulo con secciones colapsables */}
      <nav className="flex-1 px-3 py-4 overflow-y-auto" data-menu={usaMenuGhl ? 'ghl' : 'agrupado'}>
        {usaMenuGhl ? (
          <div className="space-y-1">
            {itemsAgenteGhl.length > 0 && (
              <div data-bloque="agente">
                {!collapsed && (
                  <p className="px-3 pt-1 pb-0.5 text-[11px] font-semibold uppercase tracking-wider text-gray-400">
                    Agente
                  </p>
                )}
                {itemsAgenteGhl.map(renderItemGhl)}
                <div className="my-2 border-t border-gray-200" role="separator" />
              </div>
            )}
            {SECCIONES_AGENCIA_GHL.map((s, i) => (
              <React.Fragment key={s.id}>
                {i > 0 && <div className="my-2 border-t border-gray-200" role="separator" />}
                {s.titulo && !collapsed && (
                  <p className="px-3 pt-1 pb-0.5 text-[11px] font-semibold uppercase tracking-wider text-gray-400">
                    {s.titulo}
                  </p>
                )}
                {s.items.map(renderItemGhl)}
              </React.Fragment>
            ))}
          </div>
        ) : collapsed ? (
          // Modo colapsado (solo iconos): plano, con separador fino entre grupos.
          <div className="space-y-1">
            {groupedNav.map((g, i) => (
              <React.Fragment key={g.id}>
                {i > 0 && <div className="my-2 border-t border-gray-100" />}
                {g.items.map(renderNavItem)}
              </React.Fragment>
            ))}
          </div>
        ) : (
          <div className="space-y-4">
            {groupedNav.map((g) => {
              const hasHeader = g.id !== 'principal' && g.label;
              const isOpen =
                !hasHeader ||
                g.id === activeGroup ||
                !collapsedGroups.has(g.id);
              return (
                <div key={g.id} className="space-y-1">
                  {hasHeader && (
                    <button
                      type="button"
                      onClick={() => toggleGroup(g.id)}
                      className="w-full flex items-center justify-between px-3 pt-1 pb-0.5 text-[11px] font-semibold uppercase tracking-wider text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      <span>{g.label}</span>
                      <ChevronDown
                        className={cn(
                          'h-3.5 w-3.5 transition-transform duration-200',
                          isOpen ? '' : '-rotate-90'
                        )}
                      />
                    </button>
                  )}
                  {isOpen && (
                    <div className="space-y-1">{g.items.map(renderNavItem)}</div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </nav>

      {/* GH#1643 — Configuración abajo del todo, fuera del scroll, como en GHL */}
      {usaMenuGhl && (
        <div className="px-3 py-2 border-t border-gray-200">{renderItemGhl(CONFIGURACION_GHL)}</div>
      )}

      {/* Card de días de prueba */}
      {!collapsed && user?.role !== 'SUPER_ADMIN' && trialInfo.trialEnabled && (
        <div className="px-4 pb-4">
          <div className={cn(
            "rounded-lg p-4",
            trialDaysRemaining !== null && trialDaysRemaining > 0
              ? "bg-blue-50 border border-blue-200"
              : "bg-gray-100"
          )}>
            {trialDaysRemaining !== null && trialDaysRemaining > 0 ? (
              <>
                <p className="text-sm text-blue-900 font-medium mb-1">
                  Período de prueba
                </p>
                <p className="text-xs text-blue-700 mb-3">
                  Te quedan <span className="font-bold">{trialDaysRemaining} {trialDaysRemaining === 1 ? 'día' : 'días'}</span> de prueba gratis
                </p>
                <p className="text-xs text-blue-600">
                  Disfruta todas las funcionalidades
                </p>
              </>
            ) : (
              <>
                <p className="text-sm text-gray-700 mb-3">Tu prueba gratis terminó</p>
                <button
                  onClick={() => navigate('/admin/subscription-policies?tab=plans')}
                  className="w-full bg-teal-500 hover:bg-teal-600 text-white font-medium py-2 px-4 rounded-md transition-colors"
                >
                  Elegir plan
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Footer del sidebar */}
      {!collapsed && (
        <div className="p-4 border-t border-gray-200">
          <div className="text-xs text-gray-500 text-center">
            <p>InboMarket v{APP_CONFIG.version}</p>
            <p className="mt-1">© {new Date().getFullYear()} Todos los derechos reservados</p>
          </div>
        </div>
      )}

    </div>
  );
};

// Optimización con React.memo para evitar re-renders innecesarios
const OptimizedSidebar = memo(Sidebar);

OptimizedSidebar.displayName = 'OptimizedSidebar';

export default OptimizedSidebar;
